import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withGlobalFilter } from '@bingads-webui-react/global-filter-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { siteMapPropType, CampaignType, AdGroupType } from '@bingads-webui-react/with-site-map';
import { memoize } from '@bingads-webui-universal/primitive-utilities';
import { filterMapItem, campaignTypeFilter } from './processor-helpers';

function mixedModeAdGroupFilter(adGroupType, item, permissions) {
  if (!permissions.IsDSAMixedModeCampaignEnabled) {
    return true;
  }

  if (!_.isEmpty(adGroupType)) {
    const { mixedModeAdGroup } = item;

    if (_.isFunction(mixedModeAdGroup)) {
      return mixedModeAdGroup({ AdGroupType: adGroupType }, item, permissions);
    }

    const isSearchDynamicAdGroup = adGroupType === 'SearchDynamic';
    if (isSearchDynamicAdGroup) {
      const { campaignTypes, adGroupTypes } = item;
      if (_.contains(campaignTypes, CampaignType.Default)) {
        return campaignTypeFilter(CampaignType.Default, { campaignTypes }, permissions) &&
          _.contains(adGroupTypes, AdGroupType.SearchDynamic);
      }
      return true;
    }
  }

  return true;
}

const filterItemByAdGroup = memoize((siteMap, adGroupType, permissions) =>
  filterMapItem(siteMap, _.partial(mixedModeAdGroupFilter, adGroupType), permissions));

/**
* Only for testing
*/
export const testRef = { current: undefined };

export function processMixedModeAdGroup(Component) {
  const Wrapper = ({ siteMap, ...props }) => {
    const { permissions: { IsDSAMixedModeCampaignEnabled }, permissions } = props;

    if (!IsDSAMixedModeCampaignEnabled) {
      return <Component {...props} siteMap={siteMap} />;
    }

    const processor = (adGroupType) => {
      const processedSiteMap = filterItemByAdGroup(siteMap, adGroupType, permissions);
      return (<Component {...props} siteMap={processedSiteMap} />);
    };

    return (
      <CampaignOData
        call={({ currentAdGroup }) => currentAdGroup.get()}
        escapeHatch={({ currentAdGroup }) =>
          (currentAdGroup ? undefined : {})}
        fallback={processor()}
        onError={() => processor()}
        testRef={testRef}
      >
        {adgroup => processor(_.result(adgroup, 'AdGroupType'))}
      </CampaignOData>
    );
  };

  Wrapper.propTypes = {
    siteMap: siteMapPropType.isRequired,
    scope: PropTypes.instanceOf(QueryParam).isRequired,
    permissions: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.objectOf(PropTypes.bool),
      PropTypes.number,
    ])).isRequired,
  };

  return withPermissions(withGlobalFilter(withScope(Wrapper)));
}
